import { Text } from 'src/design/atoms/Text';
import React from 'react';
import { AuthorizedButton } from 'src/app/authorization/AuthorizedButton';
import { SaveButton } from 'src/design/atoms/SaveButton';
import { resolveWorkflowButtonColor } from 'src/utils';
import { IWorkflowAuthProps, IWorkflowState } from './useWorkflowState';

export interface IWorkflowActionButtonsParams {
  currentStep: IWorkflowState['currentStep'];
  nextStep: IWorkflowState['nextStep'];
  isCreating: boolean;
  isNextStepBatchHandled: boolean;
  authProps: IWorkflowAuthProps;
  workflow: { allowDraft?: boolean };
  isAdminEffective: boolean;
  isOwner: boolean;
  disabled: boolean;
  loading: string;
  isEditable: boolean;
  doSubmit: (nextStatus: string, nextId: number) => void;
  doSaveOnly: (nextStatus: string) => void;
  stepIndex: number;
}

export interface IWorkflowActionButtons {
  DraftButton: React.ReactNode;
  SubmitButton: React.ReactNode;
  VoidButton: React.ReactNode;
  RejectButton: React.ReactNode;
  ReviseButton: React.ReactNode;
  UpdateButton: React.ReactNode;
  BatchHandledMessage: React.ReactNode;
}

/** @registry Builds the status-transition button set (draft/submit/reject/void/update) plus batch-handled messaging for WorkflowButtons. */
export function useWorkflowActionButtons({
  currentStep,
  nextStep,
  isCreating,
  isNextStepBatchHandled,
  authProps,
  workflow,
  isAdminEffective,
  isOwner,
  disabled,
  loading,
  isEditable,
  doSubmit,
  doSaveOnly,
  stepIndex,
}: IWorkflowActionButtonsParams): IWorkflowActionButtons {
  // A second status transition must not start while one is in flight.
  const isTransitioning = loading !== '';
  const isBlocked = disabled || isTransitioning;

  const DraftButton =
    isCreating && workflow.allowDraft && isEditable ? (
      <AuthorizedButton
        {...authProps}
        isOwner={isOwner}
        loading={loading === 'Draft'}
        onClick={() => doSubmit('Draft', -1)}
        color="gray"
        disabled={isBlocked}
      >
        Save Draft
      </AuthorizedButton>
    ) : null;

  // External steps have no assignable role/user (the actor is an outside
  // contact), so AuthorizedButton's own checks don't apply — empty lists are
  // its lenient "unrestricted" case, which is exactly the desired behavior.
  const submitAuthProps = currentStep?.isExternalStep
    ? {
        allowedRoles: [],
        allowedUsers: [],
        blockedUsers: [],
        allowOwner: false,
      }
    : authProps;

  const SubmitButton =
    nextStep && !isNextStepBatchHandled ? (
      <AuthorizedButton
        {...submitAuthProps}
        isOwner={isOwner}
        disabled={isBlocked}
        color={resolveWorkflowButtonColor(nextStep.buttonColor)}
        loading={loading === nextStep.label}
        onClick={() => doSubmit(nextStep.label, stepIndex + 1)}
      >
        {nextStep.buttonText}
      </AuthorizedButton>
    ) : null;

  const VoidButton =
    !isCreating && currentStep?.allowVoid ? (
      <AuthorizedButton
        {...authProps}
        isOwner={isOwner}
        color="gray"
        loading={loading === 'Voided'}
        onClick={() => doSaveOnly('Voided')}
        disabled={isBlocked}
      >
        Void
      </AuthorizedButton>
    ) : null;

  const RejectButton =
    !isCreating && nextStep?.allowReject ? (
      <AuthorizedButton
        {...authProps}
        isOwner={isOwner}
        color="red"
        loading={loading === 'Rejected'}
        onClick={() => doSubmit('Rejected', stepIndex - 1)}
        disabled={isBlocked}
      >
        Reject
      </AuthorizedButton>
    ) : null;

  const ReviseButton =
    !isCreating && nextStep?.allowRevise ? (
      <AuthorizedButton
        {...authProps}
        isOwner={isOwner}
        color="orange"
        loading={loading === 'Draft'}
        onClick={() => doSubmit('Draft', -1)}
        disabled={isBlocked}
      >
        Revise
      </AuthorizedButton>
    ) : null;

  const UpdateButton =
    !isCreating && isAdminEffective && isEditable ? (
      <SaveButton
        loading={loading === 'Saving'}
        onClick={() => doSaveOnly('Saving')}
        disabled={isBlocked}
      >
        Save Updates
      </SaveButton>
    ) : null;

  const BatchHandledMessage = isNextStepBatchHandled ? (
    <Text size="sm" c="dimmed">
      The next status update will be processed automatically by the weekly
      batch.
    </Text>
  ) : null;

  return {
    DraftButton,
    SubmitButton,
    VoidButton,
    RejectButton,
    ReviseButton,
    UpdateButton,
    BatchHandledMessage,
  };
}
